import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clapperboard, Plus, ArrowUpRight, Search, X, Lightbulb,
  Film, Camera as CameraIcon, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { useConfirm } from "@/context/ConfirmContext";

const STATUS_LABEL = {
  script_ready: "Script Ready", pre_production: "Pre-Production",
  recording: "Recording", editing: "Editing", review: "Review", ready: "Ready",
};
const STATUS_ORDER = ["script_ready", "pre_production", "recording", "editing", "review", "ready"];
const STATUS_COLORS = {
  script_ready: "bg-blue-500/10 text-blue-500",
  pre_production: "bg-yellow-500/10 text-yellow-500",
  recording: "bg-red-500/10 text-red-500",
  editing: "bg-purple-500/10 text-purple-500",
  review: "bg-orange-500/10 text-orange-500",
  ready: "bg-accent/10 text-accent",
};
const PLATFORM_LABEL = { youtube: "YouTube", instagram: "Instagram" };

const formatDate = (value) => value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";

const STATUS_PROGRESS = {
  script_ready: 20,
  pre_production: 40,
  recording: 60,
  editing: 80,
  review: 90,
  ready: 100,
};

function progressPercent(project) {
  return STATUS_PROGRESS[project.status] || 0;
}
function assetCounts(project) {
  const assets = project.assets || [];
  return { total: assets.length, video: assets.filter((a) => a.type === "video").length, photo: assets.filter((a) => a.type === "photo").length };
}

export default function Production() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [state, setState] = useState({ loading: true, error: null });
  const [projects, setProjects] = useState([]);
  const [contentList, setContentList] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [creating, setCreating] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [projectsRes, contentRes] = await Promise.all([api.get("/production"), api.get("/content")]);
      setProjects(projectsRes.data.projects || []);
      setContentList(contentRes.data.content || []);
      setState({ loading: false, error: null });
    } catch (error) { setState({ loading: false, error: apiErrorMessage(error) }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const alreadyLinkedIds = useMemo(() => new Set(projects.map((p) => p.content_id)), [projects]);
  const availableContent = useMemo(() => contentList.filter((c) => !alreadyLinkedIds.has(c.id)), [contentList, alreadyLinkedIds]);

  const createFromContent = async (contentId) => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/production", { content_id: contentId });
      toast.success("Production created");
      navigate(`/production/${data.id}`);
    } catch (error) { toast.error(apiErrorMessage(error)); }
    finally { setCreating(false); }
  };

  const deleteProject = async (e, projectId) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete this production project?",
      description: "All assets and shots linked to this production will be removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/production/${projectId}`);
      toast.success("Production deleted");
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (error) { toast.error(apiErrorMessage(error)); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = projects.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (q && !(p.title || "").toLowerCase().includes(q) && !(p.notes || "").toLowerCase().includes(q)) return false;
      return true;
    });
    const byDate = (a, b, asc) => { const av = a ? new Date(a).getTime() : 0; const bv = b ? new Date(b).getTime() : 0; return asc ? av - bv : bv - av; };
    return [...rows].sort((a, b) => {
      if (sort === "created_desc") return byDate(a.created_at, b.created_at, false);
      if (sort === "title_asc") return String(a.title || "").localeCompare(String(b.title || ""));
      if (sort === "title_desc") return String(b.title || "").localeCompare(String(a.title || ""));
      return byDate(a.updated_at || a.created_at, b.updated_at || b.created_at, false);
    });
  }, [projects, query, statusFilter, sort]);

  const inProgress = useMemo(() =>
    projects.filter((p) => p.status !== "ready")
      .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime())
      .slice(0, 1), [projects]);

  return (
    <AppShell title="Production">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Execution workspace</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground" data-testid="production-list-title">Production</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {inProgress[0] && (
            <Button variant="outline" onClick={() => navigate(`/production/${inProgress[0].id}`)}
              className="rounded-full" data-testid="production-continue-button">
              Continue <ArrowUpRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
          <Button onClick={() => setShowPicker(true)} disabled={creating} data-testid="production-new-button"
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
            <Plus className="mr-2 h-4 w-4" /> New Production
          </Button>
        </div>
      </div>

      {state.loading ? <Loader label="Loading production" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> : (
        <>
          {/* Content picker */}
          {showPicker && (
            <section className="mb-6 rounded-2xl border border-border bg-surface p-5 shadow-sm" data-testid="production-picker">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-accent">Choose content</p>
                  <h2 className="mt-1 font-display text-base font-semibold text-foreground">Start production from a content record</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowPicker(false)} className="rounded-full" data-testid="production-picker-close">
                  <X className="mr-1 h-4 w-4" /> Close
                </Button>
              </div>
              {availableContent.length === 0 ? (
                <EmptyState icon={Lightbulb} testid="production-picker-empty"
                  title="No content available"
                  description="Every content record already has a production, or none exists yet."
                  action={<Button variant="outline" className="rounded-full" onClick={() => navigate("/library")}>Go to Library</Button>} />
              ) : (
                <ul className="divide-y divide-border" data-testid="production-picker-list">
                  {availableContent.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">{c.title || "Untitled draft"}</p>
                        <p className="text-xs text-muted-foreground">{(c.platforms || []).map((p) => PLATFORM_LABEL[p] || p).join(" · ") || "No platform"}</p>
                      </div>
                      <Button size="sm" onClick={() => createFromContent(c.id)} disabled={creating}
                        className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                        data-testid={`production-picker-select-${c.id}`}>
                        Start
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {projects.length === 0 ? (
            <EmptyState icon={Clapperboard} testid="production-list-empty"
              title="No productions yet"
              description="Create a production from an existing content record to open the workspace."
              action={<Button onClick={() => setShowPicker(true)} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="production-list-empty-cta"><Plus className="mr-2 h-4 w-4" /> New Production</Button>} />
          ) : (
            <>
              {/* Filters */}
              <div className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-border bg-surface p-4 shadow-sm sm:grid-cols-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title or notes" className="pl-9 rounded-xl" data-testid="production-search-input" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-xl" data-testid="production-list-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s} data-testid={`production-list-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="rounded-xl" data-testid="production-list-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Recently updated</SelectItem>
                    <SelectItem value="created_desc">Recently created</SelectItem>
                    <SelectItem value="title_asc">Title A–Z</SelectItem>
                    <SelectItem value="title_desc">Title Z–A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Grid */}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="production-list">
                {filtered.map((p) => {
                  const counts = assetCounts(p);
                  const pct = progressPercent(p);
                  const statusColor = STATUS_COLORS[p.status] || "bg-elevated text-muted-foreground";
                  return (
                    <div key={p.id}
                      className="flex flex-col rounded-2xl border border-border bg-surface p-5 shadow-sm transition-all hover:border-accent/30 hover:shadow-md"
                      data-testid={`production-list-item-${p.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display text-base font-bold text-foreground line-clamp-2 leading-snug">{p.title || "Untitled"}</h3>
                        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${statusColor}`}
                          data-testid={`production-list-status-pill-${p.id}`}>
                          {STATUS_LABEL[p.status] || p.status}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                        {p.platform ? (
                          <span className="rounded-full bg-elevated px-2 py-0.5">{PLATFORM_LABEL[p.platform] || p.platform}</span>
                        ) : (
                          <span className="rounded-full border border-dashed border-border px-2 py-0.5">No platform</span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                          <Film className="h-2.5 w-2.5" /> {counts.video}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                          <CameraIcon className="h-2.5 w-2.5" /> {counts.photo}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                          <Clapperboard className="h-2.5 w-2.5" /> {pct}%
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-elevated">
                        <div className="h-full bg-accent transition-[width] duration-500" style={{ width: `${pct}%` }}
                          data-testid={`production-list-progress-${p.id}`} />
                      </div>

                      <p className="mt-3 text-xs text-muted-foreground">Updated {formatDate(p.updated_at)}</p>

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" onClick={() => navigate(`/production/${p.id}`)}
                            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-8 text-xs"
                            data-testid={`production-list-open-${p.id}`}>
                            Open <ArrowUpRight className="ml-1 h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/content/${p.content_id}`)}
                            className="rounded-full h-8 text-xs"
                            data-testid={`production-list-edit-${p.id}`}>
                            Edit Script
                          </Button>
                        </div>
                        <Button size="sm" variant="ghost" onClick={(e) => deleteProject(e, p.id)}
                          className="h-8 w-8 rounded-full p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          data-testid={`production-list-delete-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
       )}
    </AppShell>
  );
}

export { STATUS_LABEL as PRODUCTION_PROJECT_STATUS_LABEL };
